<template>
  <DataTable
    v-model:filters="filters"
    :value="members"
    :loading="isFetching"
    :paginator="hasLargeAmountOfMembers"
    :rows="rows"
    :filter-display="hasLargeAmountOfMembers ? 'row' : undefined"
    :global-filter-fields="['displayName', 'email']"
  >
    <template #loading>
      <ProgressSpinner
        style="width: 48px; height: 48px"
        stroke-width="3"
      />
    </template>

    <Column
      :header="$t('components.structural.organization.dialog.tabs-content.members-tab.tabs.members.table.headers.name.label')"
      field="displayName"
      :show-filter-menu="false"
    >
      <template
        #filter="{ filterModel, filterCallback }"
      >
        <InputText
          v-model="filterModel.value"
          type="text"
          :placeholder="$t('components.structural.organization.dialog.tabs-content.members-tab.tabs.members.table.headers.name.filter.placeholder')"
          @input="filterCallback()"
        />
      </template>
      <template #body="slotProps">
        <div class="flex items-center">
          <img
            :src="slotProps.data.avatar?.photoURL"
            :alt="slotProps.data.avatar?.initials"
            class="rounded-border mr-4 w-12 shadow"
          >

          <div>
            <p class="m-0">
              {{
                slotProps.data.displayName ?? slotProps.data.member.displayName
              }}
            </p>
            <p class="m-0">
              {{ slotProps.data.member.email }}
            </p>
          </div>
        </div>
      </template>
    </Column>

    <Column
      :header="$t('components.structural.organization.dialog.tabs-content.members-tab.tabs.members.table.headers.role.label')"
      field="role"
      :show-filter-menu="false"
      style="width: 20%; min-width: 13rem"
    >
      <template
        #filter="{ filterModel, filterCallback }"
      >
        <MultiSelect
          v-model="filterModel.value"
          :options="getOrganizationRolesOptions()"
          option-label="label"
          option-value="value"
          :placeholder="$t('components.structural.organization.dialog.tabs-content.members-tab.tabs.members.table.headers.role.filter.placeholder')"
          style="min-width: 14rem"
          :max-selected-labels="1"
          @change="filterCallback()"
        />
      </template>

      <template #body="slotProps">
        <template v-if="canUpdateUserRole(slotProps.data.member.uid)">
          <Select
            v-model="slotProps.data.role"
            :options="getOrganizationRolesOptions()"
            option-label="label"
            option-value="value"
            :disabled="
              (isTheOnlyOwner
                && currentUser!.uid === slotProps.data.member.uid)
                || currentUserIdRemoving === slotProps.data.member.uid
                || currentUserIdLeaving === slotProps.data.member.id
            "
            :loading="currentUserIdUpdatingRole === slotProps.data.member.uid"
            class="w-full"
            @change="
              ({ value }) => updateUserRole(slotProps.data.member.uid, value)
            "
          />
        </template>
        <template v-else>
          <p>{{ getRoleName(slotProps.data.role) }}</p>
        </template>
      </template>
    </Column>

    <component
      :is="customColumnsComponent"
      v-if="hasCustomColumns"
      v-bind="{
        hasLargeAmountOfMembers,
      }"
    />

    <!-- <Column
      style="width: 10%; min-width: 8rem"
      body-style="text-align:center"
    >
      <template #body="slotProps">
        <div
          v-if="isCurrentUser(slotProps.data.member.id)"
          v-tooltip.top="
            isTheOnlyOwner
              ? $t('components.structural.organization.dialog.tabs-content.members-tab.tabs.members.table.bodies.actions.owner-leave-tooltip')
              : ''
          "
        >
          <Button
            :label="$t('common.cta.leave')"
            size="small"
            :disabled="isTheOnlyOwner"
            :loading="currentUserIdLeaving === slotProps.data.member.id"
            @click="leaveOrganization(slotProps.data.member.id)"
          />
        </div>
        <template v-else>
          <Button
            :label="$t('common.cta.remove')"
            size="small"
            :disabled="
              !isCurrentUserOwner
                || !canRemoveUser(slotProps.data.role)
                || currentUserIdUpdatingRole === slotProps.data.member.uid
            "
            :loading="currentUserIdRemoving === slotProps.data.member.uid"
            @click="removeUser(slotProps.data.member.id)"
          />
        </template>
      </template>
    </Column> -->
  </DataTable>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { FilterMatchMode } from '@primevue/core/api'
import type { AppConfig } from '~/types/app'
import type { OrganizationMember } from '~/types/organizations/member'
import { useOrganizationsStore } from '~/store/organizations'
import { OrganizationRoles } from '~/constants/organizations'
import type {
  // LeaveOrganizationData,
  // RemoveUserFromOrganizationData,
  UpdateOrganizationUserRoleData,
} from '~/functions/types/organization'
import { SlotsComponents } from '~/constants/components/slots'
import { CloudFunctions } from '~/constants/firebase/cloud-functions'
// import { AppRoutes } from '~/constants/routes'
import { getRoleName, getOrganizationRolesOptions } from '~/utils/user/role'

const props = defineProps<{
  isFetching: boolean
  members: OrganizationMember[]
}>()

const {
  isOverridden: hasCustomColumns,
  overriddenComponent: customColumnsComponent,
} = isComponentOverridden(SlotsComponents.OrganizationDialogMembersDataTables)

const organizationsStore = useOrganizationsStore()
const { selectedOrganization } = storeToRefs(organizationsStore)

const rows = 10
const hasLargeAmountOfMembers = computed(() => props.members.length >= rows)
const appConfig = useAppConfig() as AppConfig
const filters = ref({
  displayName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  role: { value: null, matchMode: FilterMatchMode.IN },
  ...appConfig.slotsComponentsData?.[SlotsComponents.OrganizationDialogMembersDataTables]?.filters,
})

const currentUser = await getCurrentUser()
const isCurrentUser = (userId: string) => currentUser!.uid === userId
const isCurrentUserOwner = isUserGrantedInOrganization(OrganizationRoles.Owner)
const isTheOnlyOwner = computed(() => {
  return (
    isCurrentUserOwner
    && (
      props.members.filter(
        ({ member, role }) =>
          !isCurrentUser(member.id) && role >= OrganizationRoles.Owner,
      ) ?? []
    ).length === 0
  )
})
const currentUserIdUpdatingRole = ref<string | null>(null)
const currentUserIdRemoving = ref<string | null>(null)
const currentUserIdLeaving = ref<string | null>(null)

const canUpdateUserRole = (userId: string) => {
  return isCurrentUserOwner || isCurrentUser(userId)
}
const updateUserRole = async (userUid: string, role: OrganizationRoles) => {
  try {
    currentUserIdUpdatingRole.value = userUid
    await useHttpsCallable<UpdateOrganizationUserRoleData>(
      CloudFunctions.UpdateOrganizationUserRole,
      {
        organizationId: selectedOrganization.value!.id,
        userUid,
        role,
      },
    )
  }
  finally {
    currentUserIdUpdatingRole.value = null
  }
}
/* const leaveOrganization = async (userUid: string) => {
  try {
    currentUserIdLeaving.value = userUid
    await useHttpsCallable<LeaveOrganizationData>(
      CloudFunctions.LeaveOrganization,
      {
        organizationId: selectedOrganization.value!.id,
      },
    )
    navigateTo(AppRoutes.SelectOrganization)
  }
  finally {
    currentUserIdLeaving.value = null
  }
}
const canRemoveUser = async (userRole: OrganizationRoles) => {
  return !isUserGrantedInOrganization(OrganizationRoles.Owner, userRole)
}
const removeUser = async (userUid: string) => {
  try {
    currentUserIdRemoving.value = userUid
    await useHttpsCallable<RemoveUserFromOrganizationData>(
      CloudFunctions.RemoveUserFromOrganization,
      {
        organizationId: selectedOrganization.value!.id,
        userUid,
      },
    )
  }
  finally {
    currentUserIdRemoving.value = null
  }
} */
</script>
